import React, { useEffect, useState } from 'react';
import { recsAPI } from '../api';
import { TrendingUp, Eye, Heart, ShoppingBag, Activity, Users } from 'lucide-react';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const GREEN = '#166534';

const AdminRecommendations = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try { const d = await recsAPI.adminAnalytics(days); if (mounted) setData(d); }
      catch {}
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [days]);

  if (loading) return <p className="p-6 text-center text-[11px]" style={{ color: MUTED }}>Loading…</p>;
  if (!data) return <p className="p-6 text-center text-[11px]" style={{ color: MUTED }}>No data</p>;

  const { total_events, unique_users, by_type = {}, top_categories = [], top_products = [], funnel = {} } = data;

  const Card = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white border p-3" style={{ borderColor: LINE }}>
      <div className="flex items-center gap-1 mb-1">
        <Icon className="w-3 h-3" style={{ color }} strokeWidth={1.5} />
        <span className="text-[9px] tracking-[0.1em] uppercase" style={{ color: MUTED }}>{label}</span>
      </div>
      <p className="text-[16px]" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
        {value.toLocaleString('en-IN')}
      </p>
    </div>
  );

  // Conversion percentages
  const viewToWish = funnel.view ? Math.round((funnel.wishlist_add / funnel.view) * 100) : 0;
  const wishToBuy = funnel.wishlist_add ? Math.round((funnel.purchase / funnel.wishlist_add) * 100) : 0;
  const viewToBuy = funnel.view ? Math.round((funnel.purchase / funnel.view) * 100) : 0;

  return (
    <div data-testid="admin-recommendations" className="pb-6">
      {/* Window selector */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] tracking-[0.1em] uppercase" style={{ color: MUTED }}>Window:</span>
        {[7, 30, 90].map(d => (
          <button key={d} data-testid={`window-${d}d`} onClick={() => setDays(d)}
            className="px-2 py-1 text-[10px] uppercase"
            style={{
              backgroundColor: days === d ? INK : '#FFF',
              color: days === d ? '#FFF' : MUTED,
              border: `1px solid ${days === d ? INK : LINE}`,
            }}>
            {d}d
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Card icon={Activity} label="Total Events" value={total_events} color={GOLD} />
        <Card icon={Users} label="Unique Users" value={unique_users} color={GREEN} />
        <Card icon={Eye} label="Views" value={by_type.view || 0} color="#D4AF37" />
        <Card icon={Heart} label="Wishlist Adds" value={by_type.wishlist_add || 0} color="#991B1B" />
      </div>

      {/* Conversion funnel */}
      <div className="bg-white border p-3 mb-3" style={{ borderColor: LINE }}>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.5} />
          <p className="text-[10px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Conversion Funnel</p>
        </div>
        <div className="space-y-2">
          {[
            { label: 'Viewed', count: funnel.view || 0, icon: Eye, color: GOLD, pct: 100 },
            { label: 'Wishlisted', count: funnel.wishlist_add || 0, icon: Heart, color: '#991B1B', pct: viewToWish },
            { label: 'Purchased', count: funnel.purchase || 0, icon: ShoppingBag, color: GREEN, pct: viewToBuy },
          ].map(row => (
            <div key={row.label} className="flex items-center gap-2">
              <row.icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: row.color }} strokeWidth={1.5} />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[11px]" style={{ color: INK }}>{row.label}</span>
                  <span className="text-[10px]" style={{ color: MUTED }}>{row.count} users · {row.pct}%</span>
                </div>
                <div className="h-1.5 bg-gray-100 overflow-hidden">
                  <div className="h-full" style={{ width: `${Math.min(100, row.pct)}%`, backgroundColor: row.color }} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3 pt-2 border-t" style={{ borderColor: LINE }}>
          <div>
            <p className="text-[9px] tracking-[0.1em] uppercase" style={{ color: MUTED }}>View → Wishlist</p>
            <p className="text-[14px]" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>{viewToWish}%</p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.1em] uppercase" style={{ color: MUTED }}>Wishlist → Buy</p>
            <p className="text-[14px]" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>{wishToBuy}%</p>
          </div>
        </div>
      </div>

      {/* Top categories */}
      <div className="bg-white border mb-3" style={{ borderColor: LINE }}>
        <div className="px-3 py-2 border-b" style={{ borderColor: LINE }}>
          <p className="text-[10px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Top Categories</p>
        </div>
        {top_categories.length === 0 ? (
          <p className="p-4 text-center text-[11px]" style={{ color: MUTED }}>No data yet</p>
        ) : top_categories.map((c, i) => (
          <div key={c.category} className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: '#F0F0F0' }}>
            <span className="text-[11px] capitalize flex items-center gap-2" style={{ color: INK }}>
              <span className="text-[9px] w-5" style={{ color: GOLD }}>#{i + 1}</span>
              {c.category}
            </span>
            <span className="text-[10px]" style={{ color: MUTED }}>{c.events} events</span>
          </div>
        ))}
      </div>

      {/* Top products */}
      <div className="bg-white border" style={{ borderColor: LINE }}>
        <div className="px-3 py-2 border-b" style={{ borderColor: LINE }}>
          <p className="text-[10px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Top Products by Views</p>
        </div>
        {top_products.length === 0 ? (
          <p className="p-4 text-center text-[11px]" style={{ color: MUTED }}>No data yet</p>
        ) : top_products.map((p, i) => (
          <div key={p.product_id} className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: '#F0F0F0' }}>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] truncate" style={{ color: INK, fontWeight: 500 }}>
                <span className="text-[9px] mr-1" style={{ color: GOLD }}>#{i + 1}</span>
                {(p.metadata && p.metadata.name) || p.product_id}
              </p>
              <p className="text-[10px] capitalize" style={{ color: MUTED }}>{p.category}</p>
            </div>
            <span className="text-[10px] px-1.5 py-0.5" style={{ backgroundColor: 'rgba(212,175,55,0.1)', color: GOLD }}>
              {p.views} views
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminRecommendations;
